import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * PDP offer ladder v2.
 * Adds several real product variants to the cart in one request instead of
 * switching to a bundle product. Dispatches the same CartAddEvent as the
 * theme's product-form-component so cart drawer, sticky bar and PostHog keep working.
 */
class PdpOfferLadder extends HTMLElement {
  connectedCallback() {
    this.radios = Array.from(this.querySelectorAll('[data-variant-id]'));
    this.formComponent = this.querySelector('product-form-component');
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');
    this.variantInput = this.querySelector('[ref="variantId"]');
    this.formQuantityInput = this.querySelector('[data-offer-form-quantity]');
    this.addToCartButton = this.querySelector('[ref="addToCartButton"]');
    this.addToCartText = this.addToCartButton?.querySelector('.add-to-cart-text');
    this.buttonTemplate = this.dataset.buttonTemplate || '[Angebot] in den Warenkorb – [Preis]';
    this.acceleratedCheckout = this.querySelector('[data-accelerated-checkout]');
    this.acceleratedVariantInput = this.querySelector('[data-accelerated-variant-input]');
    this.productDetails = this.closest('.product-details');
    this.productMedia = this.closest('.product-information')?.querySelector('[data-testid="product-information-media"]');
    this.originalMediaGallery = this.productMedia?.querySelector('media-gallery');
    this.productPrice = this.productDetails?.querySelector('product-price');
    this.priceContainer = this.productPrice?.querySelector('[ref="priceContainer"]');
    this.priceShippingNote = this.querySelector('[data-price-shipping-note]');
    this.deliveryEstimate = this.querySelector('[data-delivery-estimate]');
    this.deliveryDateRange = this.querySelector('[data-delivery-date-range]');
    this.stickyCart = this.querySelector('[data-sticky-cart]');
    this.stickyCartTitle = this.querySelector('[data-sticky-cart-title]');
    this.stickyCartPrice = this.querySelector('[data-sticky-cart-price]');
    this.stickyCartButton = this.querySelector('[data-sticky-cart-button]');
    this.pending = null;

    if (this.productPrice && this.priceShippingNote) {
      this.productPrice.insertAdjacentElement('afterend', this.priceShippingNote);
      this.priceShippingNote.removeAttribute('hidden');
    }
    if (this.stickyCart) document.body.append(this.stickyCart);
    this.priceContainer?.setAttribute('aria-live', 'polite');

    this.radios.forEach((radio) => radio.addEventListener('change', () => this.selectOffer(radio)));
    this.setupQuantitySteppers();
    this.setupSubmit();
    this.setupStickyAddToCart();
    this.updateDeliveryEstimate();

    const selected = this.radios.find((r) => r.checked && !r.disabled) || this.radios.find((r) => !r.disabled);
    this.selectOffer(selected);
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    document.removeEventListener('cart:update', this.onCartUpdate);
    window.removeEventListener('cart:error', this.onCartError);
    this.stickyCart?.remove();
  }

  /* ---------- selection ---------- */

  selectedRadio() {
    return this.radios.find((r) => r.checked);
  }

  selectedQuantity(radio = this.selectedRadio()) {
    const card = radio?.closest('[data-offer-card]');
    const input = card?.querySelector('[data-offer-quantity-input]');
    const value = Number(input?.value || 1);
    return Math.min(Math.max(Number.isFinite(value) ? value : 1, 1), 3);
  }

  selectedItems(radio = this.selectedRadio()) {
    if (!radio) return [];
    let items = [];
    try { items = JSON.parse(radio.dataset.items || '[]'); } catch { items = []; }
    const quantity = this.selectedQuantity(radio);
    return items.map((item) => ({ id: Number(item.id), quantity: Number(item.quantity || 1) * quantity }));
  }

  selectOffer(radio) {
    if (!radio || this.pending) return;

    this.radios.forEach((candidate) => {
      const card = candidate.closest('[data-offer-card]');
      const isSelected = candidate === radio;
      candidate.checked = isSelected;
      card?.classList.toggle('is-selected', isSelected);
    });

    if (this.variantInput) this.variantInput.value = radio.dataset.variantId || '';
    if (this.formComponent && radio.dataset.productId) this.formComponent.dataset.productId = radio.dataset.productId;

    const unavailable = radio.dataset.available !== 'true' || (radio.dataset.offerKey !== 'single' && this.dataset.pricingReady !== 'true');
    this.querySelector('[data-offer-pricing-notice]')?.toggleAttribute('hidden', !unavailable);
    this.addToCartButton?.toggleAttribute('disabled', unavailable);
    this.stickyCartButton?.toggleAttribute('disabled', unavailable);

    this.updateAcceleratedCheckout(radio);
    this.updateButtonLabel(radio);
    this.updateProductSummary(radio);
    this.updateShippingNote(radio);
    this.updateSticky(radio);
    this.updateProductGallery(radio);
  }

  setupQuantitySteppers() {
    this.querySelectorAll('[data-offer-quantity]').forEach((stepper) => {
      const input = stepper.querySelector('[data-offer-quantity-input]');
      const card = stepper.closest('[data-offer-card]');
      const radio = card?.querySelector('[data-variant-id]');
      const clamp = () => {
        const value = Math.min(Math.max(Number(input.value) || 1, 1), 3);
        input.value = String(value);
        if (radio?.checked) this.selectOffer(radio);
      };
      stepper.querySelector('[data-offer-quantity-decrease]')?.addEventListener('click', (event) => {
        event.preventDefault();
        input.value = String(Number(input.value || 1) - 1);
        clamp();
      });
      stepper.querySelector('[data-offer-quantity-increase]')?.addEventListener('click', (event) => {
        event.preventDefault();
        input.value = String(Number(input.value || 1) + 1);
        clamp();
      });
      input?.addEventListener('change', clamp);
      stepper.addEventListener('click', (event) => event.stopPropagation());
    });
  }

  /* ---------- cart add ---------- */

  setupSubmit() {
    if (!this.form) return;
    // Capture on the ladder element itself: it is an ancestor of product-form-component,
    // so this runs before the theme's own submit handler and prevents a duplicate single-variant add.
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.addSelectedToCart();
    });
  }

  async addSelectedToCart() {
    const radio = this.selectedRadio();
    if (!radio || radio.dataset.available !== 'true' || this.pending) return;

    const items = this.selectedItems(radio);
    if (!items.length) return;
    if (radio.dataset.offerKey !== 'single') {
      if (this.dataset.pricingReady !== 'true') return;
      const group = crypto.randomUUID();
      items.forEach((item) => { item.properties = {
        _dwk_offer_version: '2026-09-23', _dwk_offer_group: group,
        Lesepaket: radio.dataset.offerLabel
      }; });
    }
    const quantity = this.selectedQuantity(radio);
    const sourceId = radio.dataset.variantId || '';
    const productId = radio.dataset.productId || '';
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    if (this.formQuantityInput) this.formQuantityInput.value = String(quantity);
    this.pending = { sourceId };
    this.lock(true);

    const body = JSON.stringify({ items, sections: sectionIds.join(',') });
    const cfg = fetchConfig('json', { body });

    try {
      const response = await fetch(`${Theme.routes.root}cart/add.js`, cfg);
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || Theme.translations?.addToCartError || 'Fehler beim Hinzufügen';
        this.showError(message);
        window.dispatchEvent(new CartErrorEvent(sourceId, message));
        this.formComponent?.dispatchEvent(new CartAddEvent({}, sourceId, {
          didError: true,
          source: 'product-form-component',
          itemCount: items.reduce((s, i) => s + i.quantity, 0),
          productId,
          offerStep: radio.dataset.offerKey,
          offerQuantity: quantity,
        }));
        return;
      }
      this.hideError();
      this.formComponent?.dispatchEvent(new CartAddEvent(data, sourceId, {
        source: 'product-form-component',
        itemCount: items.reduce((s, i) => s + i.quantity, 0),
        productId,
        sections: data.sections,
        offerStep: radio.dataset.offerKey,
        offerQuantity: quantity,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Hinzufügen';
      this.showError(message);
      window.dispatchEvent(new CartErrorEvent(sourceId, message));
    } finally {
      this.pending = null;
      this.lock(false);
    }
  }

  lock(locked) {
    this.setAttribute('aria-busy', locked ? 'true' : 'false');
    this.radios.forEach((r) => { if (r.dataset.available === 'true') r.disabled = locked; });
    if (this.addToCartButton) this.addToCartButton.disabled = locked || this.selectedRadio()?.dataset.available !== 'true' || (this.selectedRadio()?.dataset.offerKey !== 'single' && this.dataset.pricingReady !== 'true');
    if (this.acceleratedCheckout) this.acceleratedCheckout.inert = locked;
  }

  showError(message) {
    const el = this.querySelector('[ref="addToCartTextError"]');
    if (!el) return;
    el.classList.remove('hidden');
    const text = el.childNodes[2];
    if (text) text.textContent = message; else el.appendChild(document.createTextNode(message));
    clearTimeout(this.errorTimeout);
    this.errorTimeout = setTimeout(() => this.hideError(), 10000);
  }

  hideError() {
    this.querySelector('[ref="addToCartTextError"]')?.classList.add('hidden');
  }

  /* ---------- UI updates ---------- */

  updateAcceleratedCheckout(radio) {
    const items = this.selectedItems(radio);
    const button = this.acceleratedCheckout?.querySelector('shopify-accelerated-checkout');
    if (button && items.length) {
      button.setAttribute('variant-params', JSON.stringify(items.map((item) => ({
        id: item.id,
        quantity: item.quantity,
        requiresShipping: radio.dataset.requiresShipping !== 'false',
      }))));
    }
    if (this.acceleratedVariantInput) {
      this.acceleratedVariantInput.value = radio.dataset.variantId || '';
    }
    this.acceleratedCheckout?.toggleAttribute('hidden', radio.dataset.available !== 'true' || items.length !== 1);
  }

  formatCents(cents) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: this.dataset.currency || 'EUR' }).format(cents / 100);
  }

  totalCents(radio = this.selectedRadio()) {
    return Number(radio?.dataset.priceCents || 0) * this.selectedQuantity(radio);
  }

  updateButtonLabel(radio) {
    if (!this.addToCartText) return;
    const label = this.buttonTemplate
      .replace('[Angebot]', radio.dataset.offerLabel || '')
      .replace('[Preis]', this.formatCents(this.totalCents(radio)));
    const content = this.addToCartText.querySelector('.add-to-cart-text__content');
    if (content) content.textContent = label;
  }

  updateProductSummary(radio) {
    if (!this.priceContainer) return;
    const price = document.createElement('span');
    price.className = 'price';
    price.textContent = this.formatCents(this.totalCents(radio));
    this.priceContainer.replaceChildren();
    const valueCents = Number(radio.dataset.valueCents || 0) * this.selectedQuantity(radio);
    if (valueCents > this.totalCents(radio)) {
      const compare = document.createElement('s');
      compare.className = 'compare-at-price';
      compare.textContent = this.formatCents(valueCents);
      compare.style.marginInlineEnd = '0.4em';
      this.priceContainer.append(compare);
    }
    this.priceContainer.append(price);
  }

  updateShippingNote(radio) {
    if (!this.priceShippingNote) return;
    const shipping = radio.dataset.shippingText || '';
    this.priceShippingNote.textContent = shipping ? `inkl. MwSt. · ${shipping}` : 'inkl. MwSt.';
  }

  /* ---------- sticky bar ---------- */

  setupStickyAddToCart() {
    if (!this.stickyCart || !this.stickyCartButton || !this.addToCartButton) return;
    this.stickyDefaultLabel = this.stickyCartButton.textContent.trim();

    this.stickyCartButton.addEventListener('click', () => {
      if (this.addToCartButton.disabled || this.pending) return;
      this.stickyPending = true;
      this.setStickyState('loading', 'Wird hinzugefügt…');
      this.addToCartButton.dataset.dwkCtaLocationOverride = 'sticky';
      this.addToCartButton.click();
      delete this.addToCartButton.dataset.dwkCtaLocationOverride;
    });

    this.onCartUpdate = (event) => {
      if (!this.stickyPending || event.detail?.data?.source !== 'product-form-component') return;
      this.stickyPending = false;
      const ok = !event.detail.data.didError;
      this.setStickyState(ok ? 'added' : 'error', ok ? '✓ Hinzugefügt' : 'Nicht hinzugefügt');
      clearTimeout(this.stickyResetTimeout);
      this.stickyResetTimeout = setTimeout(() => this.resetSticky(), 2000);
    };
    this.onCartError = () => {
      if (!this.stickyPending) return;
      this.stickyPending = false;
      this.setStickyState('error', 'Nicht hinzugefügt');
      clearTimeout(this.stickyResetTimeout);
      this.stickyResetTimeout = setTimeout(() => this.resetSticky(), 2000);
    };
    document.addEventListener('cart:update', this.onCartUpdate);
    window.addEventListener('cart:error', this.onCartError);

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.stickyCart.toggleAttribute('hidden', !scrolledPast);
    }, { threshold: 0 });
    this.stickyObserver.observe(this.addToCartButton);
  }

  setStickyState(state, label) {
    if (!this.stickyCartButton) return;
    this.stickyCartButton.classList.remove('is-loading', 'is-added', 'is-error');
    if (state) this.stickyCartButton.classList.add(`is-${state}`);
    this.stickyCartButton.textContent = label;
    this.stickyCartButton.disabled = Boolean(state);
  }

  resetSticky() {
    if (!this.stickyCartButton) return;
    this.stickyCartButton.classList.remove('is-loading', 'is-added', 'is-error');
    this.stickyCartButton.textContent = this.stickyDefaultLabel || 'In den Warenkorb';
    this.stickyCartButton.disabled = this.selectedRadio()?.dataset.available !== 'true' || (this.selectedRadio()?.dataset.offerKey !== 'single' && this.dataset.pricingReady !== 'true');
  }

  updateSticky(radio) {
    if (this.stickyCartTitle) this.stickyCartTitle.textContent = radio.dataset.offerLabel || '';
    if (this.stickyCartPrice) this.stickyCartPrice.textContent = this.formatCents(this.totalCents(radio));
    if (!this.stickyPending) this.resetSticky();
  }

  updateProductGallery(radio) {
    if (!this.productMedia) return;
    const key = radio.dataset.offerKey;
    const template = this.querySelector(`template[data-bundle-gallery-template="${key}"]`);
    if (!(template instanceof HTMLTemplateElement)) return;
    const current = this.productMedia.querySelector('[data-active-offer-gallery]');
    if (current?.dataset.activeOfferKey === key) return;
    const gallery = template.content.firstElementChild?.cloneNode(true);
    if (!(gallery instanceof HTMLElement)) return;
    current?.querySelector('dialog[open]')?.close();
    this.originalMediaGallery?.setAttribute('hidden', '');
    if (current) current.replaceWith(gallery); else this.productMedia.append(gallery);
    this.setupProductGallery(gallery);
  }

  prioritizeGalleryImages(gallery) {
    const images = Array.from(gallery.querySelectorAll('[data-offer-gallery-slide] img'));
    images.forEach((image, index) => {
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.fetchPriority = index === 0 ? 'high' : 'low';
      image.decoding = 'async';
    });
  }

  setupProductGallery(gallery) {
    if (!(gallery instanceof HTMLElement) || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';
    this.prioritizeGalleryImages(gallery);

    const scroller = gallery.querySelector('[data-offer-gallery-scroller]');
    const slides = Array.from(gallery.querySelectorAll('[data-offer-gallery-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-offer-gallery-thumbnail]'));
    const dialog = gallery.querySelector('[data-offer-gallery-dialog]');
    const dialogImage = dialog?.querySelector('[data-offer-gallery-dialog-image]');
    const dialogImageWrap = dialog?.querySelector('[data-offer-gallery-dialog-image-wrap]');
    const dialogCounter = dialog?.querySelector('[data-offer-gallery-dialog-counter]');
    const dialogClose = dialog?.querySelector('[data-offer-gallery-dialog-close]');
    const dialogPrevious = dialog?.querySelector('[data-offer-gallery-dialog-previous]');
    const dialogNext = dialog?.querySelector('[data-offer-gallery-dialog-next]');
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let dialogIndex = 0;
    let dialogTrigger = null;
    let pointerStartX = 0;
    let suppressSlideClickUntil = 0;

    const selectThumbnail = (index) => {
      activeIndex = index;
      thumbnails.forEach((thumbnail, thumbnailIndex) => {
        const isSelected = thumbnailIndex === index;
        thumbnail.classList.toggle('is-selected', isSelected);
        thumbnail.setAttribute('aria-pressed', String(isSelected));
      });
    };

    const showDialogImage = (index) => {
      if (!(dialogImage instanceof HTMLImageElement) || slides.length === 0) return;
      dialogIndex = (index + slides.length) % slides.length;
      const sourceImage = slides[dialogIndex]?.querySelector('img');
      if (!(sourceImage instanceof HTMLImageElement)) return;

      dialogImageWrap?.querySelector('[data-gallery-pair]')?.remove();
      const pair = slides[dialogIndex]?.querySelector('[data-gallery-pair]');
      dialogImage.hidden = Boolean(pair);
      if (pair && dialogImageWrap) dialogImageWrap.append(pair.cloneNode(true));
      dialogImage.src = sourceImage.currentSrc || sourceImage.src;
      dialogImage.srcset = sourceImage.srcset;
      dialogImage.sizes = '100vw';
      dialogImage.alt = sourceImage.alt;
      dialogImageWrap?.classList.remove('is-zoomed');
      if (dialogCounter) dialogCounter.textContent = `Bild ${dialogIndex + 1} von ${slides.length}`;
      dialogPrevious?.toggleAttribute('hidden', slides.length < 2);
      dialogNext?.toggleAttribute('hidden', slides.length < 2);
    };

    const openDialog = (index, trigger) => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      dialogTrigger = trigger;
      showDialogImage(index);
      if (!dialog.open) dialog.showModal();
      dialogClose?.focus();
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const index = Number(thumbnail.dataset.offerGalleryThumbnail || 0);
        const slide = slides[index];
        if (!scroller || !slide) return;
        scroller.scrollTo({ left: slide.offsetLeft - slides[0].offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
        selectThumbnail(index);
      });
    });

    scroller?.addEventListener('pointerdown', (event) => {
      pointerStartX = event.clientX;
    }, { passive: true });

    scroller?.addEventListener('pointerup', (event) => {
      if (Math.abs(event.clientX - pointerStartX) > 8) suppressSlideClickUntil = Date.now() + 300;
    }, { passive: true });

    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (Date.now() < suppressSlideClickUntil) return;
        openDialog(index, slide);
      });
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openDialog(index, slide);
      });
    });

    dialogClose?.addEventListener('click', () => dialog?.close());
    dialogPrevious?.addEventListener('click', () => showDialogImage(dialogIndex - 1));
    dialogNext?.addEventListener('click', () => showDialogImage(dialogIndex + 1));
    dialogImageWrap?.addEventListener('click', () => {
      dialogImageWrap.classList.toggle('is-zoomed');
    });
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') showDialogImage(dialogIndex - 1);
      if (event.key === 'ArrowRight') showDialogImage(dialogIndex + 1);
    });
    dialog?.addEventListener('close', () => {
      dialogImageWrap?.classList.remove('is-zoomed');
      dialogTrigger?.focus();
    });

    let scrollFrame;
    scroller?.addEventListener('scroll', () => {
      cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        const index = Math.round(scroller.scrollLeft / Math.max(scroller.clientWidth, 1));
        selectThumbnail(Math.min(Math.max(index, 0), slides.length - 1));
      });
    }, { passive: true });

    selectThumbnail(activeIndex);
  }

  /* ---------- delivery estimate (unchanged from v1) ---------- */

  updateDeliveryEstimate() {
    if (!this.deliveryEstimate || !this.deliveryDateRange) return;
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23',
      }).formatToParts(new Date()).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value])
    );
    const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    const weekday = today.getUTCDay();
    const isBusinessDay = weekday >= 1 && weekday <= 5;
    const cutoffHour = Number(this.deliveryEstimate.dataset.cutoffHour || 13);
    const earliest = isBusinessDay && Number(parts.hour) < cutoffHour ? 1 : 2;
    const addBusinessDays = (start, days) => {
      const d = new Date(start); let added = 0;
      while (added < days) { d.setUTCDate(d.getUTCDate() + 1); const w = d.getUTCDay(); if (w !== 0 && w !== 6) added += 1; }
      return d;
    };
    const fmt = new Intl.DateTimeFormat('de-DE', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit' });
    this.deliveryDateRange.textContent = `${fmt.format(addBusinessDays(today, earliest))} – ${fmt.format(addBusinessDays(today, 3))}`;
  }
}

if (!customElements.get('pdp-offer-ladder')) {
  customElements.define('pdp-offer-ladder', PdpOfferLadder);
}
